import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import ApplicationCard from "@/components/ApplicationCard";
import { useApplications, useApplicationDistricts } from "@/lib/queries";
import { useTrackView } from "@/lib/useTrackView";
import { breadcrumbSchema } from "@/lib/schema";
import FinalCta from "@/sections/FinalCta";

const ALL = "Tümü";

function FilterRow({ label, options, value, onChange, testid }) {
  if (options.length <= 1) return null;
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <span className="shrink-0 pt-1.5 font-oswald text-[11px] uppercase tracking-[0.2em] text-neutral-500 sm:w-24">{label}</span>
      <div className="flex flex-wrap gap-2">
        {[ALL, ...options].map((o) => (
          <button
            key={o}
            data-testid={`${testid}-${o === ALL ? "all" : o}`}
            onClick={() => onChange(o)}
            className={`border px-3.5 py-1.5 font-oswald text-xs uppercase tracking-[0.08em] transition-colors ${
              value === o ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-white"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Applications() {
  useTrackView("application", "list");
  const { data: items = [] } = useApplications();
  const { data: appDistricts = [] } = useApplicationDistricts();
  const [service, setService] = useState(ALL);
  const [district, setDistrict] = useState(ALL);
  const [brand, setBrand] = useState(ALL);
  const [model, setModel] = useState(ALL);

  const opts = useMemo(() => {
    const s = new Set(), d = new Set(), b = new Set();
    items.forEach((i) => {
      (i.services || []).forEach((x) => s.add(x));
      if (i.district) d.add(i.district);
      if (i.brand) b.add(i.brand);
    });
    return { services: [...s].sort(), districts: [...d].sort(), brands: [...b].sort() };
  }, [items]);

  const models = useMemo(() => {
    const m = new Set();
    items.filter((i) => brand === ALL || i.brand === brand).forEach((i) => i.model && m.add(i.model));
    return [...m].sort();
  }, [items, brand]);

  const filtered = items.filter(
    (i) =>
      (service === ALL || (i.services || []).includes(service)) &&
      (district === ALL || i.district === district) &&
      (brand === ALL || i.brand === brand) &&
      (model === ALL || i.model === model)
  );

  return (
    <>
      <Seo
        title="Gerçek Araç Uygulamaları | Meguiar's İzmit"
        description="Atölyemizden geçen gerçek araçlar: seramik kaplama, PPF, detaylı iç temizlik ve daha fazlasının öncesi/sonrası sonuçları. Marka, model, hizmet ve ilçeye göre inceleyin."
        canonicalPath="/uygulamalar"
        jsonLd={breadcrumbSchema([{ label: "Ana Sayfa", to: "/" }, { label: "Uygulamalar" }])}
      />
      <PageHero
        overline="Uygulamalar"
        title="Gerçek Araç Uygulamaları"
        subtitle="Atölyemizden geçen gerçek araçların dönüşüm hikâyeleri; uygulanan hizmetler, kullanılan yöntemler ve öncesi/sonrası sonuçlar."
        breadcrumbs={[{ label: "Ana Sayfa", to: "/" }, { label: "Uygulamalar" }]}
      />

      {/* District quick links */}
      {appDistricts.length > 0 && (
        <div className="border-b border-white/10 bg-black/60">
          <div className="container-x flex flex-wrap items-center gap-2.5 py-4">
            <span className="font-oswald text-[11px] uppercase tracking-[0.2em] text-gold">Bölgeye göre</span>
            {appDistricts.map((d) => (
              <Link
                key={d.district_slug}
                to={`/uygulamalar/${d.district_slug}`}
                data-testid={`applications-district-link-${d.district_slug}`}
                className="inline-flex items-center gap-1.5 border border-white/10 bg-white/[0.02] px-3.5 py-1.5 text-sm text-neutral-300 transition-colors hover:border-gold/40 hover:text-gold"
              >
                <MapPin className="h-3.5 w-3.5 text-gold/70" /> {d.district} <span className="text-xs text-neutral-500">({d.count})</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <section className="obsidian py-14 sm:py-20">
        <div className="container-x">
          {/* Filters */}
          <div className="space-y-4 border border-white/10 bg-white/[0.02] p-5 sm:p-7">
            <FilterRow label="Hizmet" options={opts.services} value={service} onChange={setService} testid="filter-service" />
            <FilterRow label="İlçe" options={opts.districts} value={district} onChange={setDistrict} testid="filter-district" />
            <FilterRow label="Marka" options={opts.brands} value={brand} onChange={(v) => { setBrand(v); setModel(ALL); }} testid="filter-brand" />
            <FilterRow label="Model" options={models} value={model} onChange={setModel} testid="filter-model" />
          </div>

          <p className="mt-6 font-oswald text-xs uppercase tracking-[0.2em] text-neutral-500" data-testid="applications-count">
            {filtered.length} uygulama
          </p>

          {filtered.length === 0 ? (
            <p className="mt-10 text-neutral-500">Bu filtrelere uygun uygulama bulunamadı.</p>
          ) : (
            <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((c, i) => (
                <ApplicationCard key={c.slug} app={c} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
      <FinalCta />
    </>
  );
}
